import { MESApiService, spParameter } from "@dexteel/mesf-core";
import type { Recipe } from "../models/Recipe";
import type { RecipeLayer } from "../models/RecipeLayer";
import { LAYER_MOVE_MODE, type LayerMoveMode } from "../models/LayerMoveMode";

export const getRecipes = async (
  OnlyEnabled?: boolean | null,
  OnlyCurrent?: boolean | null,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "OnlyEnabled", value: OnlyEnabled ?? true },
    { name: "OnlyCurrent", value: OnlyCurrent ?? false },
  ];

  return await apiService.callV2("[SCRAP].[GetRecipes]", parameters, signal);
};

export const getRecipeLayers = async (
  recipeId: number,
  chargeNumber?: number,
  signal?: AbortSignal,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "RecipeId", value: recipeId },
    { name: "ChargeNumber", value: chargeNumber ?? 1 },
  ];

  return await apiService.callV2(
    "[SCRAP].[GetRecipeLayers]",
    parameters,
    signal,
  );
};

export const upsertRecipe = async (recipe: Recipe) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "RecipeId", value: recipe.RecipeId ?? null },
    { name: "RecipeName", value: recipe.RecipeName ?? null },
    { name: "Author", value: recipe.Author ?? null },
    { name: "Comments", value: recipe.Comments ?? null },
    { name: "IsEnable", value: recipe.IsEnable ?? null },
    { name: "IsActive", value: recipe.IsActive ?? null },
  ];

  return await apiService.callV2("[SCRAP].[UpsertRecipe]", parameters);
};

export const deleteRecipe = async (recipeId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [{ name: "RecipeId", value: recipeId }];

  return await apiService.callV2("[SCRAP].[DeleteRecipe]", parameters);
};

export const deleteRecipeFamily = async (originalRecipeId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "OriginalRecipeId", value: originalRecipeId },
  ];

  return await apiService.callV2("[SCRAP].[DeleteRecipe]", parameters);
};

export const setRecipeFamilyEnable = async (
  originalRecipeId: number,
  isEnable: boolean,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "OriginalRecipeId", value: originalRecipeId },
    { name: "IsEnable", value: isEnable },
  ];

  return await apiService.callV2("[SCRAP].[SetRecipeFamilyEnable]", parameters);
};

export const upsertRecipeLayer = async (recipeLayer: RecipeLayer) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "RecipeLayerId", value: recipeLayer.RecipeLayerId ?? null },
    { name: "ChargeNumber", value: recipeLayer.ChargeNumber },
    { name: "RecipeId", value: recipeLayer.RecipeId ?? null },
    { name: "LayerNumber", value: recipeLayer.LayerNumber ?? null },
    { name: "CommodityId", value: recipeLayer.CommodityId ?? null },
    { name: "Weight", value: recipeLayer.Weight ?? null },
  ];

  return await apiService.callV2("[SCRAP].[UpsertRecipeLayer]", parameters);
};

export const deleteRecipeLayer = async (recipeLayerId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "RecipeLayerId", value: recipeLayerId },
  ];

  return await apiService.callV2("[SCRAP].[DeleteRecipeLayer]", parameters);
};

export const reorderRecipeLayer = async (
  recipeLayerId: number,
  newLayerNumber: number,
  mode: LayerMoveMode = LAYER_MOVE_MODE.REORDER,
) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "RecipeLayerId", value: recipeLayerId },
    { name: "NewLayerNumber", value: newLayerNumber },
    { name: "Mode", value: mode },
  ];

  return await apiService.callV2("[SCRAP].[ReorderRecipeLayer]", parameters);
};

/**
 * Creates a NEW VERSION within the same recipe family
 * @param recipeId - The source recipe to upgrade
 */
export const upgradeRecipe = async (recipeId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "RecipeId", value: recipeId },
    { name: "IsOriginal", value: false },
  ];

  return await apiService.callV2("[SCRAP].[CloneRecipe]", parameters);
};

/**
 * Creates a NEW INDEPENDENT recipe (clone with new family)
 * @param recipeId - The source recipe to clone
 * @param recipeName - Optional custom name (if not provided, uses original name + " (Clone)")
 */
export const cloneRecipe = async (recipeId: number, recipeName?: string) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "RecipeId", value: recipeId },
    { name: "IsOriginal", value: true },
    { name: "RecipeName", value: recipeName ?? null },
  ];

  return await apiService.callV2("[SCRAP].[CloneRecipe]", parameters);
};

export const setRecipeAsCurrent = async (recipeId: number) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [{ name: "RecipeId", value: recipeId }];

  return await apiService.callV2("[SCRAP].[SetRecipeAsCurrent]", parameters);
};
